// =====================================================================
// Inventaire.jsx - Pagina principala M-004 cu 3 tabs interne (19-05-2026)
// =====================================================================
// Layout Option C: 1 link in sidebar (/inventaire), 3 vue-uri interne.
//
// Tabs:
//   1. Encoder (default)  - listeaza InventoryCount + filtre + buton "+ Nouveau"
//   2. Facturation        - pivot client x (item x date) + checkbox bulk
//   3. Items (catalog)    - listeaza 75 InventoryItem + buton "+ Nouveau item"
//
// Pattern: state-based tab switching (NU URL routing).
// Modale: B2.1b (urmatoarea etapa). Butoanele "+ Nouveau" afiseaza alert temporar.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  inventoryItems as itemsApi,
  inventoryCounts as countsApi,
  projects as projectsApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import ItemFormModal from './ItemFormModal.jsx';
import ComptageFormModal from './ComptageFormModal.jsx';

const PAGE_SIZE = 80;

const COUNT_TYPE_LABELS_FR = {
  MID_MONTH: 'Mi-mois',
  END_MONTH: 'Fin mois',
  AD_HOC:    'Ad-hoc',
};

const COUNT_TYPE_BADGE_STYLE = {
  MID_MONTH: { background: '#dbeafe', color: '#1e40af' },
  END_MONTH: { background: '#e0e7ff', color: '#3730a3' },
  AD_HOC:    { background: '#f3f4f6', color: '#374151' },
};

const INVOICED_BADGE_STYLE = {
  yes: { background: '#d1fae5', color: '#065f46' },
  no:  { background: '#fef3c7', color: '#92400e' },
};

const inputBase = {
  padding: '0.4rem 0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: '0.8125rem',
  background: '#fff',
};

const btnPrimary = {
  background: '#2563eb',
  color: '#fff',
  border: 0,
  padding: '0.5rem 1rem',
  borderRadius: 4,
  fontWeight: 500,
  fontSize: '0.875rem',
  cursor: 'pointer',
};

const btnSuccess = {
  background: '#059669',
  color: '#fff',
  border: 0,
  padding: '0.5rem 1rem',
  borderRadius: 4,
  fontWeight: 500,
  fontSize: '0.875rem',
  cursor: 'pointer',
};

const btnPager = (disabled) => ({
  padding: '0.25rem 0.5rem',
  background: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  fontSize: '0.875rem',
});

const exportBtnStyle = (format, loading) => ({
  padding: '0.375rem 0.75rem',
  background: loading ? '#f1f5f9' : (format === 'xlsx' ? '#16a34a' : '#3b82f6'),
  color: loading ? '#94a3b8' : 'white',
  border: '1px solid ' + (loading ? '#cbd5e1' : (format === 'xlsx' ? '#15803d' : '#2563eb')),
  borderRadius: 4,
  fontSize: '0.8125rem',
  fontWeight: 500,
  cursor: loading ? 'wait' : 'pointer',
});

const thStyle = {
  padding: '0.625rem 0.75rem',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  textAlign: 'left',
  borderBottom: '1px solid #e5e7eb',
};

const tdStyle = {
  padding: '0.625rem 0.75rem',
  verticalAlign: 'middle',
  color: '#374151',
  fontSize: '0.875rem',
};

const tabBtn = (active) => ({
  background: 'transparent',
  border: 0,
  padding: '0.75rem 1.25rem',
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 400,
  color: active ? '#2563eb' : '#6b7280',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  cursor: 'pointer',
  marginBottom: -1,
});

function formatDateFR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return '';
  }
}

function formatNumber(n) {
  if (n === null || n === undefined) return '';
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(num)) return '';
  return num.toLocaleString('fr-FR');
}

// =====================================================================
// MAIN COMPONENT
// =====================================================================
export default function Inventaire() {
  const { can } = useAuth();

  const canReadItems   = can('inventory.read.all');
  const canCreateItem  = can('inventory.create.all');
  const canReadCounts  = can('inventory_counts.read.all');
  const canCreateCount = can('inventory_counts.create.all') || can('inventory_counts.create.own');
  const canInvoice     = can('inventory_counts.update.all');

  const [activeTab, setActiveTab] = useState('encoder');
  const [projects, setProjects]   = useState([]);

  useEffect(() => {
    projectsApi.list({ isActive: true })
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(err => console.error('[Inventaire] projects fetch:', err));
  }, []);

  if (!canReadItems && !canReadCounts) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Inventaire mensuel</h1>
        <p style={{ color: '#6b7280', marginTop: 16 }}>Permission refusée.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Inventaire mensuel</h1>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>M-004 — bilunaire</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
        Comptages bilunaires d'inventaire (15 du mois + fin de mois).
      </p>

      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {canReadCounts && (
          <button style={tabBtn(activeTab === 'encoder')} onClick={() => setActiveTab('encoder')}>
            📝 Encoder
          </button>
        )}
        {canReadCounts && (
          <button style={tabBtn(activeTab === 'facturation')} onClick={() => setActiveTab('facturation')}>
            💰 Facturation
          </button>
        )}
        {canReadItems && (
          <button style={tabBtn(activeTab === 'items')} onClick={() => setActiveTab('items')}>
            📦 Items (catalogue)
          </button>
        )}
      </div>

      {activeTab === 'encoder'     && canReadCounts && <EncoderTab projects={projects} canCreate={canCreateCount} />}
      {activeTab === 'facturation' && canReadCounts && <FacturationTab projects={projects} canInvoice={canInvoice} />}
      {activeTab === 'items'       && canReadItems  && <ItemsTab projects={projects} canCreate={canCreateItem} />}
    </div>
  );
}

// =====================================================================
// TAB 1: ENCODER
// =====================================================================
function EncoderTab({ projects, canCreate }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [exporting, setExporting] = useState(null); // 'csv' | 'xlsx' | null

  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterDateFrom, setFilterDateFrom]   = useState('');
  const [filterDateTo, setFilterDateTo]       = useState('');
  const [filterInvoiced, setFilterInvoiced]   = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  function openCreate() { setEditingId(null); setShowModal(true); }
  function openEdit(id) { setEditingId(id); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditingId(null); }
  function onSaved()    { closeModal(); fetchList(); }

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (filterProjectId) params.projectId = filterProjectId;
      if (filterDateFrom)  params.dateFrom  = filterDateFrom;
      if (filterDateTo)    params.dateTo    = filterDateTo;
      if (filterInvoiced)  params.invoiced  = filterInvoiced;

      const res = await countsApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('[EncoderTab] fetch error:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, filterProjectId, filterDateFrom, filterDateTo, filterInvoiced]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Export handler (CSV sau XLSX) - foloseste filtre curente
  async function handleExport(format) {
    setExporting(format);
    try {
      const params = new URLSearchParams({ format });
      if (filterProjectId) params.append('projectId', filterProjectId);
      if (filterDateFrom)  params.append('dateFrom', filterDateFrom);
      if (filterDateTo)    params.append('dateTo', filterDateTo);
      if (filterInvoiced)  params.append('invoiced', filterInvoiced);

      const response = await fetch(`/api/inventory-counts/export?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errData.message || "Erreur lors de l'export");
      }
      const cd = response.headers.get('Content-Disposition') || '';
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1] : `inventaire.${format}`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Inventaire] export error', err);
      alert(err.message || "Erreur lors de l'export");
    } finally {
      setExporting(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select value={filterProjectId} onChange={e => { setFilterProjectId(e.target.value); setPage(1); }} style={{ ...inputBase, minWidth: 180 }}>
          <option value="">Tous les projets</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <input type="date" value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }} style={inputBase} title="Date début" />
        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>→</span>
        <input type="date" value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(1); }} style={inputBase} title="Date fin" />

        <select value={filterInvoiced} onChange={e => { setFilterInvoiced(e.target.value); setPage(1); }} style={{ ...inputBase, minWidth: 130 }}>
          <option value="">Tous statuts</option>
          <option value="false">Non facturés</option>
          <option value="true">Facturés</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{total} comptage{total !== 1 ? 's' : ''}</span>
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting !== null || loading || total === 0}
            style={exportBtnStyle('csv', exporting === 'csv')}
            title="Exporter en CSV (UTF-8 BOM, compatible Excel)"
          >
            {exporting === 'csv' ? '⏳ Export…' : `📥 Exporter CSV (${total})`}
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || loading || total === 0}
            style={exportBtnStyle('xlsx', exporting === 'xlsx')}
            title="Exporter en XLSX avec mise en forme"
          >
            {exporting === 'xlsx' ? '⏳ Export…' : '📊 Exporter XLSX'}
          </button>
          {canCreate && (
            <button style={btnPrimary} onClick={openCreate}>
              + Nouveau comptage
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Réf</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Item</th>
                <th style={thStyle}>Projet</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Quantité</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Statut</th>
                <th style={thStyle}>Encodé par</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun comptage à afficher.</td></tr>
              )}
              {items.map(c => (
                <tr key={c.id} onClick={() => openEdit(c.id)} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>{c.ref}</td>
                  <td style={tdStyle}>{formatDateFR(c.date)}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{c.item?.name}</div>
                    <div style={{ fontSize: '0.6875rem', color: '#9ca3af', fontFamily: 'monospace' }}>{c.item?.ref}</div>
                  </td>
                  <td style={tdStyle}>{c.item?.project?.name}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>
                    {formatNumber(c.quantity)} <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{c.item?.unit}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...COUNT_TYPE_BADGE_STYLE[c.countType], padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
                      {COUNT_TYPE_LABELS_FR[c.countType] || c.countType}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...(c.invoiced ? INVOICED_BADGE_STYLE.yes : INVOICED_BADGE_STYLE.no), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
                      {c.invoiced ? '✓ Facturé' : 'Non facturé'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: '0.8125rem', color: '#6b7280' }}>{c.countedBy?.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <button style={btnPager(page === 1)} disabled={page === 1} onClick={() => setPage(page - 1)}>‹ Précédent</button>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Page {page} sur {totalPages}</span>
          <button style={btnPager(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(page + 1)}>Suivant ›</button>
        </div>
      )}
    
      {showModal && (
        <ComptageFormModal countId={editingId} onClose={closeModal} onSaved={onSaved} />
      )}

      </>
  );
}

// =====================================================================
// TAB 2: FACTURATION - pivot table
// =====================================================================
function FacturationTab({ projects, canInvoice }) {
  const [counts, setCounts]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy]       = useState(false);

  const [year, setYear]   = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo   = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const params = { dateFrom, dateTo, pageSize: 500 };
      const res = await countsApi.list(params);
      setCounts(res.items || []);
    } catch (err) {
      console.error('[FacturationTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setCounts([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // Build pivot
  const pivot = (() => {
    const byProject = new Map();
    for (const c of counts) {
      const pid = c.item?.project?.id || 'unknown';
      const pname = c.item?.project?.name || '—';
      const iid = c.itemId;
      const iname = c.item?.name || c.item?.ref || iid;
      const dateKey = (c.date || '').slice(0, 10);

      if (!byProject.has(pid)) byProject.set(pid, { id: pid, name: pname, items: new Map(), allInvoiced: true, countIds: [] });
      const proj = byProject.get(pid);
      proj.countIds.push(c.id);
      if (!c.invoiced) proj.allInvoiced = false;

      if (!proj.items.has(iid)) proj.items.set(iid, { id: iid, name: iname, unit: c.item?.unit, byDate: new Map() });
      const item = proj.items.get(iid);
      if (!item.byDate.has(dateKey)) item.byDate.set(dateKey, { quantity: 0, countIds: [], allInvoiced: true });
      const cell = item.byDate.get(dateKey);
      cell.quantity += parseFloat(c.quantity) || 0;
      cell.countIds.push(c.id);
      if (!c.invoiced) cell.allInvoiced = false;
    }
    return Array.from(byProject.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const allDates = (() => {
    const s = new Set();
    for (const c of counts) {
      const d = (c.date || '').slice(0, 10);
      if (d) s.add(d);
    }
    return Array.from(s).sort();
  })();

  function toggleProject(projectRow) {
    const next = new Set(selectedIds);
    const allSelected = projectRow.countIds.every(id => next.has(id));
    if (allSelected) projectRow.countIds.forEach(id => next.delete(id));
    else projectRow.countIds.forEach(id => next.add(id));
    setSelectedIds(next);
  }

  function toggleSelectAll() {
    if (selectedIds.size === counts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(counts.map(c => c.id)));
  }

  async function handleBulkInvoice() {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Marquer ${selectedIds.size} comptage(s) comme facturé(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await countsApi.bulkInvoice({ ids: Array.from(selectedIds) });
      alert(`Facturés: ${res.invoiced}\nDéjà facturés (skip): ${res.alreadyInvoiced}\nIntrouvables: ${res.notFound}`);
      await fetchCounts();
    } catch (err) {
      console.error('[bulk invoice]', err);
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  async function handleBulkUninvoice() {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Annuler la facturation pour ${selectedIds.size} comptage(s) ?`)) return;
    setBulkBusy(true);
    try {
      const res = await countsApi.bulkUninvoice({ ids: Array.from(selectedIds) });
      alert(`Réversés: ${res.uninvoiced}\nDéjà non facturés (skip): ${res.alreadyUninvoiced}\nIntrouvables: ${res.notFound}`);
      await fetchCounts();
    } catch (err) {
      console.error('[bulk uninvoice]', err);
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally { setBulkBusy(false); }
  }

  const years = [year - 1, year, year + 1];
  const months = [
    { v: 1, l: 'Janvier' }, { v: 2, l: 'Février' }, { v: 3, l: 'Mars' }, { v: 4, l: 'Avril' },
    { v: 5, l: 'Mai' }, { v: 6, l: 'Juin' }, { v: 7, l: 'Juillet' }, { v: 8, l: 'Août' },
    { v: 9, l: 'Septembre' }, { v: 10, l: 'Octobre' }, { v: 11, l: 'Novembre' }, { v: 12, l: 'Décembre' },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>Période:</span>
        <select value={month} onChange={e => setMonth(parseInt(e.target.value, 10))} style={inputBase}>
          {months.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        <select value={year} onChange={e => setYear(parseInt(e.target.value, 10))} style={inputBase}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{selectedIds.size} sélectionné{selectedIds.size !== 1 ? 's' : ''}</span>
          {canInvoice && (
            <>
              <button style={{ ...btnSuccess, opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkInvoice}>
                ✓ Marquer facturé
              </button>
              <button style={{ ...btnPager(false), opacity: (selectedIds.size === 0 || bulkBusy) ? 0.5 : 1 }} disabled={selectedIds.size === 0 || bulkBusy} onClick={handleBulkUninvoice}>
                Annuler facturation
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && counts.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 6 }}>
          Aucun comptage pour cette période.
        </div>
      )}

      {!loading && counts.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ ...thStyle, width: 36 }}>
                  <input type="checkbox" checked={selectedIds.size === counts.length && counts.length > 0} onChange={toggleSelectAll} />
                </th>
                <th style={thStyle}>Client / Item</th>
                {allDates.map(d => (
                  <th key={d} style={{ ...thStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDateFR(d)}</th>
                ))}
                <th style={{ ...thStyle, textAlign: 'center' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {pivot.map(proj => (
                <ProjectRows key={proj.id} proj={proj} allDates={allDates} selectedIds={selectedIds} onToggleProject={() => toggleProject(proj)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ProjectRows({ proj, allDates, selectedIds, onToggleProject }) {
  const totalSelected = proj.countIds.filter(id => selectedIds.has(id)).length;
  const allSelected = totalSelected === proj.countIds.length && proj.countIds.length > 0;
  const someSelected = totalSelected > 0 && totalSelected < proj.countIds.length;

  return (
    <>
      <tr style={{ borderTop: '2px solid #e5e7eb', background: '#fafafa' }}>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem' }}>
          <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={onToggleProject} disabled={proj.allInvoiced} title={proj.allInvoiced ? 'Tous déjà facturés' : ''} />
        </td>
        <td style={{ ...tdStyle, padding: '0.5rem 0.75rem', fontWeight: 600, color: '#111827' }}>{proj.name}</td>
        {allDates.map(d => <td key={d} style={tdStyle}></td>)}
        <td style={{ ...tdStyle, textAlign: 'center' }}>
          <span style={{ ...(proj.allInvoiced ? INVOICED_BADGE_STYLE.yes : INVOICED_BADGE_STYLE.no), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
            {proj.allInvoiced ? '✓ Tous facturés' : 'Non facturé'}
          </span>
        </td>
      </tr>
      {Array.from(proj.items.values()).map(item => (
        <tr key={item.id} style={{ borderTop: '1px solid #f3f4f6' }}>
          <td style={{ ...tdStyle, padding: '0.4rem 0.75rem' }}></td>
          <td style={{ ...tdStyle, padding: '0.4rem 0.75rem 0.4rem 2rem', color: '#374151', fontSize: '0.8125rem' }}>
            {item.name} <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>({item.unit})</span>
          </td>
          {allDates.map(d => {
            const cell = item.byDate.get(d);
            return (
              <td key={d} style={{ ...tdStyle, padding: '0.4rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: cell ? 500 : 400, color: cell ? (cell.allInvoiced ? '#9ca3af' : '#111827') : '#d1d5db' }}>
                {cell ? formatNumber(cell.quantity) : '—'}
              </td>
            );
          })}
          <td style={tdStyle}></td>
        </tr>
      ))}
    </>
  );
}

// =====================================================================
// TAB 3: ITEMS - catalog
// =====================================================================
function ItemsTab({ projects, canCreate }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterSearch, setFilterSearch]       = useState('');
  const [filterActive, setFilterActive]       = useState('true');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  function openCreate() { setEditingId(null); setShowModal(true); }
  function openEdit(id) { setEditingId(id); setShowModal(true); }
  function closeModal() { setShowModal(false); setEditingId(null); }
  function onSaved()    { closeModal(); fetchList(); }

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (filterProjectId) params.projectId = filterProjectId;
      if (filterSearch)    params.search    = filterSearch;
      if (filterActive)    params.isActive  = filterActive;

      const res = await itemsApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('[ItemsTab] fetch:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, filterProjectId, filterSearch, filterActive]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select value={filterProjectId} onChange={e => { setFilterProjectId(e.target.value); setPage(1); }} style={{ ...inputBase, minWidth: 180 }}>
          <option value="">Tous les projets</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <input type="text" placeholder="Recherche par nom / réf..." value={filterSearch} onChange={e => { setFilterSearch(e.target.value); setPage(1); }} style={{ ...inputBase, minWidth: 220 }} />

        <select value={filterActive} onChange={e => { setFilterActive(e.target.value); setPage(1); }} style={{ ...inputBase, minWidth: 120 }}>
          <option value="true">Actifs</option>
          <option value="false">Inactifs</option>
          <option value="">Tous</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{total} item{total !== 1 ? 's' : ''}</span>
          {canCreate && (
            <button style={btnPrimary} onClick={openCreate}>
              + Nouvel item
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: 4, marginBottom: '1rem' }}>{error}</div>}
      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Réf</th>
                <th style={thStyle}>Nom</th>
                <th style={thStyle}>Projet</th>
                <th style={thStyle}>Unité</th>
                <th style={thStyle}>Statut</th>
                <th style={thStyle}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Aucun item à afficher.</td></tr>
              )}
              {items.map(it => (
                <tr key={it.id} onClick={() => openEdit(it.id)} style={{ borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>{it.ref}</td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{it.name}</td>
                  <td style={tdStyle}>{it.project?.name || '—'}</td>
                  <td style={tdStyle}>{it.unit}</td>
                  <td style={tdStyle}>
                    <span style={{ ...(it.isActive ? INVOICED_BADGE_STYLE.yes : { background: '#f3f4f6', color: '#6b7280' }), padding: '0.125rem 0.5rem', borderRadius: 12, fontSize: '0.6875rem', fontWeight: 500 }}>
                      {it.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#9ca3af', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <button style={btnPager(page === 1)} disabled={page === 1} onClick={() => setPage(page - 1)}>‹ Précédent</button>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Page {page} sur {totalPages}</span>
          <button style={btnPager(page === totalPages)} disabled={page === totalPages} onClick={() => setPage(page + 1)}>Suivant ›</button>
        </div>
      )}
    
      {showModal && (
        <ItemFormModal itemId={editingId} onClose={closeModal} onSaved={onSaved} />
      )}

      </>
  );
}
